import {
  ExceptionGroup,
  ExceptionGroupExtension,
  ExceptionMutationAction,
  ExceptionType,
  ExceptionStatus,
  useListExceptionGroupsQuery,
} from "~/operations";
import { ExceptionsFilterVariables } from "~/pages/shared/policy/hooks/usePolicyExceptionsFilterContext";

type UseGetExceptionsParams = {
  mrn?: string;
  scopeMrn: string;
  types: ExceptionType[];
  skip?: boolean;
  exceptionId?: string;
  includeChildScopes?: boolean;
  includeDeleted?: boolean;
  includeExtension?: boolean;
  filterVariables?: ExceptionsFilterVariables;
};

type UseGetExceptionsReturn = {
  exceptionGroups: ExceptionGroup[];
  pendingExceptionsGroups: ExceptionGroup[];
  pendingAndReviewedExceptionGroups: ExceptionGroup[];
  extensions: ExceptionGroupExtension[];
  exceptionsCount: number;
  loadingListExceptionGroup: boolean;
  refetch: () => void;
};

export function useFetchExceptions({
  scopeMrn,
  types,
  mrn,
  skip = false,
  exceptionId,
  includeChildScopes = false,
  includeDeleted = false,
  includeExtension = false,
  filterVariables = {},
}: UseGetExceptionsParams): UseGetExceptionsReturn {
  const {
    data: exceptionGroupsResult,
    loading,
    refetch,
  } = useListExceptionGroupsQuery({
    variables: {
      input: {
        mrn,
        scopeMrn,
        types,
        filter: {
          id: exceptionId,
          includeDeleted,
          includeExtension,
          ...filterVariables.filter,
        },
        includeChildScopes,
      },
    },
    skip,
    fetchPolicy: "cache-and-network",
  });

  const exceptionGroups = (
    exceptionGroupsResult?.listExceptionGroups?.edges?.flatMap((e) => e.node) ||
    []
  ).filter(
    (group) => group.action !== ExceptionMutationAction.OutOfScope,
  ) as ExceptionGroup[];

  const pendingExceptionsGroups = exceptionGroups.filter(
    (e) => e.status === ExceptionStatus.New,
  );
  const pendingAndReviewedExceptionGroups = exceptionGroups.filter(
    (e) =>
      e.status === ExceptionStatus.New ||
      e.status === ExceptionStatus.Approved ||
      e.status === ExceptionStatus.NeedsExtension,
  );

  const extensions: ExceptionGroupExtension[] = exceptionGroups.flatMap(
    (group) => group.extensions ?? [],
  );

  // Count the total number of exceptions
  const exceptionsCount = pendingAndReviewedExceptionGroups.length;

  return {
    refetch,
    pendingExceptionsGroups,
    pendingAndReviewedExceptionGroups,
    exceptionGroups,
    extensions,
    exceptionsCount,
    loadingListExceptionGroup: loading,
  };
}
